import { computed, ref, watch } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { constant } from '@/constant';
import type {
  BlockType,
  ExpansionPageKey,
  ProjectExpansionPageItem,
  ProjectExpansionPagePayload,
} from '@/themes/types';
import { expansionPageKeys } from '@/themes/types';
import { newsItemsService } from '@/services/newsItems.service';

import { getModuleTocId } from '@/utils/toc-utils';
import { useProjectPage } from './useProjectPage';

const expansionPageModuleTypes: Record<ExpansionPageKey, BlockType> = {
  [expansionPageKeys.NewsItem]: constant.Block.Type.News,
  [expansionPageKeys.EventItem]: constant.Block.Type.Event,
};

type UseProjectExpansionPageOptions = {
  slug: string;
  isTemplate: boolean;
  moduleId: string;
  itemId: string;
  expansionKey: ExpansionPageKey;
};

export function useProjectExpansionPage({
  slug,
  isTemplate,
  moduleId,
  itemId,
  expansionKey,
}: UseProjectExpansionPageOptions) {
  const projectPage = useProjectPage(slug, isTemplate);
  const isItemLoading = ref(false);
  const item = ref<ProjectExpansionPageItem | null>(null);

  const moduleIdNumber = computed(() => Number.parseInt(moduleId, 10));
  const itemIdNumber = computed(() => Number.parseInt(itemId, 10));
  const expectedModuleType = computed(() => expansionPageModuleTypes[expansionKey]);

  const module = computed(() => {
    if (!Number.isFinite(moduleIdNumber.value)) {
      return null;
    }

    const match = projectPage.modules.value.find((entry) => entry.id === moduleIdNumber.value) ?? null;
    if (!match) {
      return null;
    }

    return match.block_type === expectedModuleType.value ? match : null;
  });

  const backTo = computed<RouteLocationRaw>(() => ({
    name: isTemplate ? 'projectTemplate' : 'project',
    params: { slug },
    hash: module.value?.id ? `#${getModuleTocId(module.value.project_id, module.value.id)}` : undefined,
  }));

  watch(
    [() => projectPage.project.value?.id, module, itemIdNumber],
    async ([projectId, currentModule, currentItemId], _previousValue, onCleanup) => {
      let cancelled = false;
      onCleanup(() => {
        cancelled = true;
      });

      item.value = null;

      if (!projectId || !currentModule?.id || !Number.isFinite(currentItemId)) {
        isItemLoading.value = false;
        return;
      }

      isItemLoading.value = true;

      try {
        switch (expansionKey) {
          case expansionPageKeys.NewsItem: {
            const items = await newsItemsService.getModuleNewsItems(currentModule.id);
            if (cancelled) {
              return;
            }

            item.value = items.find((candidate) => candidate.id === currentItemId) ?? null;
            break;
          }
          case expansionPageKeys.EventItem:
            item.value = null;
            break;
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load project expansion item:', error);
        }
      } finally {
        if (!cancelled) {
          isItemLoading.value = false;
        }
      }
    },
    { immediate: true },
  );

  const expansion = computed<ProjectExpansionPagePayload>(() => ({
    key: expansionKey,
    module: module.value,
    itemId: Number.isFinite(itemIdNumber.value) ? itemIdNumber.value : null,
    item: item.value,
    backTo: backTo.value,
  }));

  return {
    ...projectPage,
    expansion,
    canRenderPage: computed(
      () =>
        Boolean(projectPage.project.value) &&
        Boolean(module.value) &&
        Boolean(item.value) &&
        (isTemplate || projectPage.userAccess.value.allowed),
    ),
    isLoading: computed(() => projectPage.isLoading.value || isItemLoading.value),
    loadExpansionPage: async () => {
      await projectPage.loadProject();
    },
  };
}
